import styled from 'styled-components';
import { Radio, Checkbox } from 'antd';

const GREY_250 = '#BFBFBF';

const COLORS = {
	primary: '#2F54EB',
	text: {
		primary: '#262626',
		disabled: '#bfbfbf'
	},
	border: {
		default: '#d9d9d9',
		light: '#f0f0f0'
	},
	background: {
		light: '#f8f8f8',
		lighter: '#f5f5f5',
		primary: '#ebeefd'
	}
} as const;

const SPACING = {
	xs: '8px',
	sm: '12px',
	md: '16px',
	lg: '24px'
} as const;

const TRANSITIONS = {
	default: 'all 0.2s'
} as const;

export const StyledCheckbox = styled(Checkbox)`
	&.ant-checkbox-wrapper {
		display: flex;
		align-items: flex-start;

		&:hover {
			.ant-checkbox-inner {
				border-color: ${COLORS.primary};
			}
		}
	}

	.ant-checkbox {
		top: 2px;
	}

	.ant-checkbox-checked .ant-checkbox-inner {
		background-color: ${COLORS.primary};
		border-color: ${COLORS.primary};
	}

	&.ant-checkbox-wrapper-disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

export const Container = styled.div`
	display: flex;
	align-items: center;
	background: ${COLORS.background.light};
	border-radius: 8px;
	padding: ${SPACING.sm} ${SPACING.lg};
	width: 100%;
	justify-content: space-between;
`;

export const Section = styled.div`
	flex: 1;
	text-align: center;
	font-size: 15px;
	font-weight: 500;
	color: #222;
`;

export const Divider = styled.div`
	width: 1px;
	height: ${SPACING.lg};
	background: ${COLORS.border.light};
	margin: 0 ${SPACING.md};
`;

export const CheckboxContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.md};
	margin-top: ${SPACING.md};

	.ant-checkbox-wrapper {
		margin-left: 0px;
		padding: 8px;
		gap: 8px;
		margin-left: 0;
	}

	.ant-checkbox-disabled + span span {
		color: ${GREY_250};
	}

	#action_plans_selected_columns {
		width: 100% !important;
	}

	#action_plans_selected_columns .ant-checkbox-group {
		width: 100% !important;
	}
`;

export const CheckboxColumns = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: ${SPACING.lg};
	width: 100%;
	align-items: start;
	margin-top: ${SPACING.xs};

	@media (max-width: 1599px) {
		grid-template-columns: repeat(3, 1fr);

		> *:nth-child(4) {
			display: none;
		}
	}

	.ant-checkbox-group {
		display: flex;
		flex-direction: column;
		gap: ${SPACING.xs};
	}
`;

export const Column = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.xs};

	.ant-typography {
		margin-bottom: ${SPACING.xs};
	}
`;

export const StyledRadioGroup = styled(Radio.Group)`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: ${SPACING.md};
	width: 100%;
`;

export const ToolsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 24px;
	width: 100%;

	@media (max-width: 1340px) {
		grid-template-columns: repeat(2, 1fr);
	}

	.ant-checkbox-wrapper {
		margin: 0;
		display: flex;
		align-items: center;
	}

	.ant-checkbox-wrapper span:nth-of-type(2) {
		width: 100%;
	}

	.ant-checkbox {
		display: none !important;
	}

	.ant-checkbox + span {
		padding-left: 0 !important;
		padding-right: 0 !important;
	}
`;

export const ToolContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

export const LeftContent = styled.div`
	display: flex;
	align-items: center;
	flex: 1;
`;

export const IconWrapper = styled.div`
	flex-shrink: 0;
	width: ${SPACING.lg};
	height: ${SPACING.lg};
	margin-right: ${SPACING.sm};
	display: flex;
	align-items: center;
	justify-content: center;

	svg,
	i {
		width: 100%;
		height: 100%;
		vertical-align: middle;
	}

	.ant-checkbox-wrapper-checked &,
	.ant-checkbox-wrapper-checked:hover & {
		svg,
		i {
			color: ${COLORS.primary} !important;
		}
	}

	.ant-checkbox-wrapper-disabled & {
		svg,
		i {
			color: ${COLORS.text.disabled} !important;
		}
	}
`;

export const NameWrapper = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: ${COLORS.text.primary};
	flex-grow: 1;

	.ant-radio-button-wrapper:hover:not(.ant-radio-button-wrapper-checked):not(.ant-radio-button-wrapper-disabled) & {
		color: ${COLORS.primary} !important;
	}

	.ant-radio-button-wrapper-checked & {
		color: ${COLORS.primary} !important;
	}

	.ant-radio-button-wrapper-disabled & {
		color: ${COLORS.text.disabled} !important;
	}
`;

export const CustomReportsSpan = styled.span`
	background: ${COLORS.border.light};
	color: ${COLORS.text.primary};
	padding: 2px 6px;
	border-radius: 6px;
	min-width: 48px;
	min-height: 16px;
	text-align: center;
	border: 1px solid transparent;

	.ant-radio-button-wrapper:hover:not(.ant-radio-button-wrapper-checked):not(.ant-radio-button-wrapper-disabled) & {
		color: ${COLORS.primary} !important;
		border-color: ${COLORS.primary} !important;
	}

	.ant-radio-button-wrapper-checked & {
		border-color: ${COLORS.primary} !important;
		background: ${COLORS.background.primary};
		color: ${COLORS.primary} !important;
	}

	.ant-radio-button-wrapper-disabled & {
		background: ${COLORS.background.lighter};
		color: ${COLORS.text.disabled} !important;
		border-color: transparent !important;
	}
`;

export const StyledCheckboxGroup = styled(Checkbox.Group)`
	width: 100%;
`;

export const StyledCheckboxContent = styled.div`
	height: auto;
	max-height: 56px;
	padding: 10px ${SPACING.md};
	border: 1px solid ${COLORS.border.default};
	border-radius: 8px;
	background: #ffffff;
	transition: ${TRANSITIONS.default};
	width: 100%;
	margin-left: ${SPACING.xs};

	.ant-checkbox-wrapper:hover:not(.ant-checkbox-wrapper-disabled) & {
		border-color: ${COLORS.primary};

		${NameWrapper}, ${CustomReportsSpan} {
			color: ${COLORS.primary} !important;
		}

		${CustomReportsSpan} {
			border-color: ${COLORS.primary} !important;
		}

		${IconWrapper} svg,
		${IconWrapper} i {
			color: ${COLORS.primary} !important;
		}
	}

	.ant-checkbox-wrapper-checked & {
		background: ${COLORS.background.primary};
		border-color: ${COLORS.background.primary};

		${NameWrapper}, ${CustomReportsSpan} {
			color: ${COLORS.primary} !important;
		}

		${CustomReportsSpan} {
			border-color: ${COLORS.primary} !important;
			background: ${COLORS.background.primary};
		}

		${IconWrapper} svg,
		${IconWrapper} i {
			color: ${COLORS.primary} !important;
		}
	}

	.ant-checkbox-wrapper-disabled & {
		background: none;
		border-color: ${COLORS.border.light};
		cursor: not-allowed;

		${NameWrapper} {
			color: ${COLORS.text.disabled} !important;
		}

		${CustomReportsSpan} {
			background: ${COLORS.background.lighter};
			color: ${COLORS.text.disabled} !important;
			border-color: transparent !important;
		}

		${IconWrapper} svg,
		${IconWrapper} i {
			color: ${COLORS.text.disabled} !important;
		}
	}
`;

export const ResponsiveCheckbox = styled(Checkbox)`
	&.lg-responsive {
		display: none;
	}

	&.md-responsive {
		display: flex;
	}

	@media (min-width: 1600px) {
		&.lg-responsive {
			display: flex;
		}

		&.md-responsive {
			display: none;
		}
	}
`;

export const ColumnContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.xs};
`;
